import React from 'react'
import { format, isValid } from 'date-fns'
import { useParams } from 'react-router-dom'
import MarketingHeader from '../components/marketing/MarketingHeader'
import Seo from '../components/Seo'
import { showConsentSettings } from '../utils/cookieConsentConfig'

// Simple slug→import mapping. For MVP this is enough; later we can use import.meta.glob.
const registry: Record<string, any> = {
  'shared-parenting-practice': () => import('../../content/blog/shared-parenting-practice.mdx'),
  '10-shared-parenting-challenges': () => import('../../content/blog/10-shared-parenting-challenges.mdx'),
  'shared-parenting-research': () => import('../../content/blog/shared-parenting-research.mdx'),
  'parenting-plan-shared-care': () => import('../../content/blog/parenting-plan-shared-care.mdx'),
  'parenting-agreement-template': () => import('../../content/blog/parenting-agreement-template.mdx'),
}

const BlogPostPage: React.FC = () => {
  const { slug = '' } = useParams()
  const importer = registry[slug]

  const [mod, setMod] = React.useState<any | null>(null)
  const articleRef = React.useRef<HTMLDivElement | null>(null)
  const [toc, setToc] = React.useState<Array<{ id: string; text: string; level: number }>>([])
  React.useEffect(() => {
    let cancelled = false
    if (!importer) return
    importer().then((m: any) => {
      if (!cancelled) setMod(m)
    })
    return () => { cancelled = true }
  }, [slug])

  if (!importer) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <MarketingHeader />
        <main className="flex-grow">
          <div className="max-w-3xl mx-auto px-4 py-8">Article not found.</div>
        </main>
      </div>
    )
  }

  const FM = (mod && mod.frontmatter) ? mod.frontmatter : {}
  const Body = mod ? mod.default : null
  const [minutes, setMinutes] = React.useState<number | null>(null)
  const [seoDescription, setSeoDescription] = React.useState<string>('')

  function truncateAtWord(text: string, maxLen: number): string {
    const t = (text || '').replace(/\s+/g, ' ').trim()
    if (t.length <= maxLen) return t
    let cut = t.slice(0, maxLen)
    const lastSpace = cut.lastIndexOf(' ')
    if (lastSpace > Math.floor(maxLen * 0.6)) cut = cut.slice(0, lastSpace)
    return cut.replace(/[\s\.,;:!\-]+$/, '') + '…'
  }
  React.useEffect(() => {
    // After rendering the content, count words from visible text and estimate reading time
    const el = articleRef.current
    if (!el) return
    const rAF = requestAnimationFrame(() => {
      try {
        const text = el.innerText || ''
        const words = (text.match(/\S+/g) || []).length
        const mins = Math.max(2, Math.round(words / 200))
        setMinutes(mins)
        if (!FM.description) {
          setSeoDescription(truncateAtWord(text, 155))
        }
      } catch {}
    })
    return () => cancelAnimationFrame(rAF)
  }, [mod])

  React.useEffect(() => {
    const root = articleRef.current
    if (!root) return
    const nodes = Array.from(root.querySelectorAll('h2, h3')) as HTMLHeadingElement[]
    const items = nodes.map(h => ({ id: h.id, text: h.textContent || '', level: h.tagName === 'H2' ? 2 : 3 }))
    setToc(items.filter(i => i.id))
  }, [mod])

  const canonical = `https://2homes.app/blog/${FM.slug}`
  const ogImage = (FM.ogImage as string)?.startsWith('/') ? `https://2homes.app${FM.ogImage}` : FM.ogImage
  const pageTitle = truncateAtWord(`${FM.title || 'Article'} | Blog 2homes.app`, 60)

  React.useEffect(() => {
    if (FM.description) {
      setSeoDescription(truncateAtWord(FM.description as string, 155))
    }
  }, [FM.description])
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: FM.title,
    description: FM.description,
    author: { '@type': 'Organization', name: '2homes.app' },
    datePublished: FM.date,
    dateModified: FM.updated || FM.date,
    mainEntityOfPage: canonical,
    image: ogImage ? [ogImage] : undefined,
  }
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Blog', item: 'https://2homes.app/blog' },
      { '@type': 'ListItem', position: 2, name: FM.title, item: canonical },
    ],
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <MarketingHeader />
      <Seo
        title={pageTitle}
        description={seoDescription}
        canonical={canonical}
        robots="index,follow"
        openGraph={{
          title: FM.title,
          description: seoDescription,
          url: canonical,
          type: 'article',
          image: ogImage,
        }}
      />
      {mod && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />}
      {mod && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />}
      <main className="flex-grow">
        <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 md:grid-cols-[1fr_280px] gap-6">
          <article ref={articleRef as any} className="max-w-none">
            {!mod ? (
              <div>Loading…</div>
            ) : (
              <>
                {/* Offset and typography for headings inside the article */}
                <style>{`
                  article h2, article h3 { scroll-margin-top: 96px; }
                  article h2 { margin-top: 1.25rem; margin-bottom: 0.5rem; font-weight: 700; }
                  article h3 { margin-top: 1rem; margin-bottom: 0.5rem; font-weight: 700; }
                  article .prose a { color: #2563eb; text-decoration: underline; text-underline-offset: 2px; }
                  article .prose a:hover { color: #1d4ed8; }
                  /* Ensure bullet and ordered list markers are visible */
                  article .prose ul { list-style-type: disc; padding-left: 1.25rem; }
                  article .prose ol { list-style-type: decimal; padding-left: 1.25rem; }
                  article .prose ul > li, article .prose ol > li { margin-left: 0; }
                  article .prose ul > li::marker, article .prose ol > li::marker { color: currentColor; }
                `}</style>
                {/* Hero image */}
                {FM.ogImage && (
                  <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden border bg-slate-100">
                    <img src={FM.ogImage} alt={FM.title} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
                  </div>
                )}
                {/* Title + meta */}
                <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">{FM.title}</h1>
                <p className="mt-2 text-sm text-slate-500">
                  {(() => {
                    const d = new Date(FM.updated || FM.date);
                    return isValid(d) ? format(d, 'dd MMM yyyy') : '';
                  })()} • {(minutes ?? Math.max(2, Math.round((((FM.description as string) || '').split(/\s+/).length || 600) / 200)))} min read
                </p>
                {/* Body */}
                <div className="prose prose-slate max-w-none mt-6">
                  {Body && <Body />}
                </div>
              </>
            )}
          </article>
          {mod && toc.length > 0 && (
            <aside className="hidden md:block sticky top-24 self-start not-prose">
              <div className="p-4 rounded-lg border bg-white text-sm">
                <div className="font-semibold mb-2">Table of contents</div>
                <ul className="space-y-2">
                  {toc.map((i) => (
                    <li key={i.id} className={i.level === 3 ? 'ml-3' : ''}>
                      <a className="hover:underline text-slate-700 font-semibold" href={`#${i.id}`}>{i.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          )}
        </div>
      </main>
      <footer className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-300">&copy; {new Date().getFullYear()} 2homes.app - Shared parenting calendar for parents and caregivers</p>
          <div className="mt-3 flex items-center justify-center gap-2 text-slate-400 text-xs flex-wrap">
            <a href="/terms" className="hover:underline">Terms of service</a>
            <span>•</span>
            <a href="/privacy-policy" className="hover:underline">Privacy policy</a>
            <span>•</span>
            <button
              type="button"
              className="hover:underline bg-transparent border-0 text-xs text-slate-400 cursor-pointer p-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                showConsentSettings();
              }}
            >
              Manage consent
            </button>
            <span>•</span>
            <a href="/contact" className="hover:underline">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default BlogPostPage


